import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import styles from "./AboutDesigner.module.scss";
import { Container, Icon } from "semantic-ui-react";
import { ProfileData } from "../../../types/siteTypes";
import {
  formatEur,
  getPatternPricingPresentation,
} from "../../../utils/patternPricing";
import { getThumbnailUrl } from "../../../utils/imageUtils";

type Props = {
  profile: ProfileData;
  compact?: boolean;
};

const AboutDesigner: React.FC<Props> = ({
  profile: { photo, bio, company, location, social, website, handle, products },
  compact,
}) => {
  const socialLinks = social ? Object.keys(social).filter((k) => (social as any)[k]) : [];

  return (
    <div className={compact ? styles.wrapperCompact : styles.wrapper}>
      {/* ── Cover strip ── */}
      <div className={styles.cover}>
        <div className={styles.coverText}>
          <span className={styles.coverLabel}>
            <FormattedMessage id="profile.designedBy" defaultMessage="Designed by" />
          </span>
          <h2 className={styles.coverHandle}>{handle}</h2>
        </div>
      </div>

      <Container>
        {/* ── Avatar + name card ── */}
        <Link to={`/seller/${handle}`} className={styles.profileLink}>
        <div className={styles.card}>
            <div className={styles.avatarWrap}>
              <img
                src={getThumbnailUrl(photo?.url) || "/photos/no-photo.jpg"}
                alt={handle}
                className={styles.avatar}
              />
            </div>

            <div className={styles.identity}>
              <h1 className={styles.handle}>{handle}</h1>
              {bio && <p className={styles.bio}>{bio}</p>}
            </div>

          {/* ── Meta row ── */}
          <div className={styles.meta}>
            {company && (
              <span className={styles.metaItem}>
                <Icon name="building outline" />
                {company}
              </span>
            )}
            {location && (
              <span className={styles.metaItem}>
                <Icon name="map marker alternate" />
                {location}
              </span>
            )}
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.metaItem} ${styles.metaLink}`}
              >
                <Icon name="linkify" />
                {website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>

          {/* ── Social icons ── */}
          {socialLinks.length > 0 && (
            <div className={styles.social}>
              {socialLinks.map((k) => (
                <a
                  key={k}
                  href={(social as any)[k]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label={k}
                >
                  <Icon name={k as any} size="large" fitted />
                </a>
              ))}
            </div>
          )}
        </div>   
        </Link>

        {/* ── Patterns grid ── */}
        {products && products.length > 0 && (
          <section className={styles.patternsSection}>
            <h2 className={styles.sectionTitle}>
              <FormattedMessage id="nav.patterns" defaultMessage="Patterns" />
              <span className={styles.count}>{products.length}</span>
            </h2>
            <div className={styles.patternsGrid}>
              {products.map((pattern) => {
                const {
                  showPromotionInfo,
                  promotionBasePrice,
                  currentEffectivePrice,
                  lowestEffectivePrice,
                  showOmnibusInfo,
                } = getPatternPricingPresentation(pattern);

                return (
                  <Link
                    to={`/pattern/${pattern._id}`}
                    key={pattern._id}
                    className={styles.patternCard}
                  >
                    <div className={styles.patternImage}>
                      <img
                        src={
                          getThumbnailUrl(
                            pattern.mainPhoto?.url || pattern.photos?.[0]?.url
                          ) || "/photos/no-photo.jpg"
                        }
                        alt={pattern.name}
                      />
                    </div>
                    <div className={styles.patternInfo}>
                      <p className={styles.patternName}>{pattern.name}</p>
                      <div className={styles.patternPrice}>
                        {showPromotionInfo ? (
                          <>
                            <span className={styles.original}>
                              {formatEur(promotionBasePrice)}
                            </span>
                            <span className={styles.discounted}>
                              {formatEur(currentEffectivePrice)}
                            </span>
                          </>
                        ) : (
                          <span className={styles.price}>
                            {formatEur(currentEffectivePrice)}
                          </span>
                        )}
                      </div>
                      {showOmnibusInfo && lowestEffectivePrice !== null && (
                        <div className={styles.omnibusLine}>
                          <FormattedMessage
                            id="profile.patterns.lowestPriceNote"
                            defaultMessage="Lowest price in the last 30 days before discount: {price}"
                            values={{
                              price: formatEur(lowestEffectivePrice),
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </Container>
    </div>
  );
};

export default AboutDesigner;

